import { ClearGlassFrame } from "@/components/ui/frames/clear-glass-frame";
import { RemoveIcon, SearchIcon } from "@/icons";
import { Suspense, useEffect, useState } from "react";
import { PlayerItem } from "prisma-layer";
import { ItemLoadingSkeleton, ItemMiniItem } from "./item-mini-item";
import { listInventoryItems } from "@/services/inventory/list-inventory-items";
import { useAuth } from "@/providers/auth-provider";
import { isTMA } from "@tma.js/sdk";
import { useRawInitData } from "@tma.js/sdk-react";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { LoadingOverlay } from "@/components/ui/overlays/loading-overlay";
import { Alert } from "@/components/alert/alert";

export const InventoryItemsView = () => {
  const { getAccessToken } = useAuth();
  const { selectedItems, setSelectedItems, setCurrentRoute } = useSPAContext();

  const [items, setItems] = useState<
    Pick<PlayerItem, "id" | "item_name" | "amount">[] | undefined
  >();

  const [isMultiSelecting, setIsMultiSelecting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const accessToken = getAccessToken();

      if (!accessToken) {
        console.error("❌ No access token available");
        setError("Authentication failed. Please try logging in again.");
        return;
      }

      const response = await listInventoryItems(accessToken);

      setItems(response.data?.player_items ?? []);
    } catch (err) {
      console.error("❌ Failed to fetch items:", err);
      setError("Failed to load your items. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Filter items based on search query
  const filteredItems =
    items?.filter((item) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      return (
        item.item_name.toLowerCase().includes(query) ||
        item.id.toString().includes(query)
      );
    }) || [];

  const isSelected = (
    item: Pick<PlayerItem, "id" | "item_name" | "amount">,
  ) => {
    return selectedItems.some((selected) => selected.id === item.id);
  };

  const handleClickItem = (
    item: Pick<PlayerItem, "id" | "item_name" | "amount">,
  ) => {
    if (isMultiSelecting) {
      handleSelectItem(item);
    } else {
      handleSelectItem(item);
    }
  };

  const handleLongPressItem = (
    item: Pick<PlayerItem, "id" | "item_name" | "amount">,
  ) => {
    if (isMultiSelecting) {
      handleSelectItem(item);
      return;
    }

    setIsMultiSelecting(true);
    setSelectedItems([item]);
  };

  const handleSelectItem = (
    item: Pick<PlayerItem, "id" | "item_name" | "amount">,
  ) => {
    if (isSelected(item)) {
      if (selectedItems.length === 1) {
        setIsMultiSelecting(false);
      }
      setSelectedItems(
        selectedItems.filter((selected) => selected.id !== item.id),
      );
      return;
    }

    setSelectedItems([...selectedItems, item]);
  };

  const handleClickSend = () => {
    if (selectedItems.length === 0) {
      Alert("You must select at least 1 item to send");
      return;
    }

    setCurrentRoute(RouteNames.SEND_ITEMS);
  };

  const handleClickCancel = () => {
    setSelectedItems([]);
    setIsMultiSelecting(false);
  };

  return (
    <div className="flex flex-col gap-2 w-full h-full max-h-full pb-24 pt-4 px-2 bg-pk-night-1/80 backdrop-blur-[2px]">
      <StandardFrame variant="gray" className=" min-h-0 h-fit">
        {/* Search Section */}
        <div className="border-3 w-full border-pk-gray-2 rounded-md h-full">
          <div className="flex items-center justify-between h-full gap-2 px-4 py-1 border-3 bg-pk-gray-2/50 border-pk-gray-4 rounded-md">
            <input
              type="text"
              placeholder="Search by Name or Id"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="outline-none leading-none min-w-[100px] placeholder:text-xs flex-1 max-w-[200px]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="p-1 hover:bg-pk-gray-3 rounded-sm transition-colors"
                aria-label="Clear search"
              >
                <RemoveIcon width={14} height={14} fill="#f4eae7" />
              </button>
            )}
            <SearchIcon width={16} height={16} stroke="#f4eae7" />
          </div>
        </div>
      </StandardFrame>

      {isMultiSelecting ? (
        <div className="flex flex-row items-center justify-between gap-2 bg-pk-gray-1 p-1 rounded-sm z-10 ">
          <SimpleButton variant="gray" onClick={handleClickCancel}>
            <span className="text-xs xs:text-sm">Cancel</span>
          </SimpleButton>
          <SimpleButton variant="gray" onClick={handleClickSend}>
            <span className="text-xs xs:text-sm">Send To</span>
          </SimpleButton>
          <div className="text-xs xs:text-sm text-pk-gray-4">
            {selectedItems.length} item(s) selected
          </div>
        </div>
      ) : (
        <div className=" flex items-center justify-start px-2 text-[10px] xs:text-sm text-pk-gray-4/70">
          Press and hold an item to see more options
        </div>
      )}

      <StandardFrame
        variant="gray"
        className="min-h-[250px] transition-all duration-300 relative"
      >
        {/* Loading Overlay */}
        <LoadingOverlay isVisible={isLoading} message="Loading items..." />

        {/* Item List */}
        <div className="h-full flex flex-col z-10">
          <div
            className={`grid grid-cols-3 xs:grid-cols-4 gap-2 w-full mx-auto rounded-lg overflow-y-auto`}
          >
            {/* Item Components */}
            {error ? (
              <div className="col-span-full flex flex-col items-center justify-center py-8 text-center">
                <div className="text-pk-red-400 mb-4">
                  <SearchIcon width={48} height={48} stroke="currentColor" />
                </div>
                <p className="text-pk-gray-3 text-sm mb-4">{error}</p>
                <SimpleButton variant="gray" onClick={fetchItems}>
                  Try Again
                </SimpleButton>
              </div>
            ) : items === undefined ? (
              Array.from({ length: 10 }).map((_, index) => (
                <ItemLoadingSkeleton key={`item-loading-skeleton-${index}`} />
              ))
            ) : filteredItems.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-8 text-center">
                <div className="text-pk-gray-4 mb-4">
                  <SearchIcon width={48} height={48} stroke="currentColor" />
                </div>
                <p className="text-pk-gray-3 text-sm">
                  {searchQuery
                    ? `No items found matching "${searchQuery}"`
                    : "No items in your inventory"}
                </p>
              </div>
            ) : (
              filteredItems.map((item) => (
                <ItemMiniItem
                  key={item.id}
                  itemName={item.item_name}
                  amount={item.amount}
                  onClick={() => handleClickItem(item)}
                  onLongPress={() => handleLongPressItem(item)}
                  selected={isSelected(item)}
                />
              ))
            )}
          </div>
        </div>
      </StandardFrame>
    </div>
  );
};
